import { createContext, PropsWithChildren, Suspense, useCallback, useContext, useEffect, useMemo, useRef } from "react"
import { RequestTakeOverDialog } from "@/modules/common/dialog/request-take-over-dialog"
import {
  PatientFileLockTakeOverResult,
  PatientFileLockTakeOverStatus,
} from "@/modules/patients/services/usePatientFileLockChangeSubscription"
import { useReleasePatientFileLockMutation } from "@/modules/patients/services/useReleasePatientFileLockMutation"
import { useRequestTakeOverPatientMutation } from "@/modules/patients/services/useRequestTakeOverPatientMutation"

type UpdateWizardPatientFileLockContextValue = {
  handleOpenChange: (isOpen: boolean) => void
  takePatientFileLock: () => Promise<PatientFileLockTakeOverStatus>
}

const UpdateWizardPatientFileLockContext = createContext<UpdateWizardPatientFileLockContextValue | null>(null)

type UpdateWizardPatientFileLockProviderProps = PropsWithChildren<{
  patientId: string
  patientName: string
  shouldHideCallReason: boolean
  onOpenChange: (isOpen: boolean) => void
}>

const UpdateWizardPatientFileLockProvider = ({
  children,
  patientId,
  patientName,
  shouldHideCallReason,
  onOpenChange,
}: UpdateWizardPatientFileLockProviderProps) => {
  const releasePatientFileLock = useReleasePatientFileLockMutation()
  const [requestTakeOverPatient] = useRequestTakeOverPatientMutation()
  const { waitForPatientFileLock, reportPatientFileLockTakeOver } = useWaitForPatientFileLock()

  const isPatientFileLockNeeded = !shouldHideCallReason

  const hasAskedForPatientFileLockRef = useRef(false)

  const handlePatientFileLockTakeOverEnded = useCallback(
    ({ status }: PatientFileLockTakeOverResult) => reportPatientFileLockTakeOver(status),
    [reportPatientFileLockTakeOver],
  )

  const takePatientFileLock = useCallback(() => {
    if (!isPatientFileLockNeeded) {
      return Promise.resolve<PatientFileLockTakeOverStatus>("GRANTED")
    }

    hasAskedForPatientFileLockRef.current = true

    const patientFileLockTaken = waitForPatientFileLock()

    requestTakeOverPatient(
      { patientId, appointmentId: undefined },
      () => reportPatientFileLockTakeOver("GRANTED"),
      undefined,
      () => reportPatientFileLockTakeOver("ABANDONED"),
    )

    return patientFileLockTaken
  }, [
    requestTakeOverPatient,
    patientId,
    isPatientFileLockNeeded,
    waitForPatientFileLock,
    reportPatientFileLockTakeOver,
  ])

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen && isPatientFileLockNeeded && !hasAskedForPatientFileLockRef.current) {
        releasePatientFileLock({ patientId, shouldIncreaseMissedCall: false })
      }
      onOpenChange(isOpen)
    },
    [releasePatientFileLock, patientId, onOpenChange, isPatientFileLockNeeded],
  )

  const updateWizardPatientFileLock = useMemo(
    () => ({ handleOpenChange, takePatientFileLock }),
    [handleOpenChange, takePatientFileLock],
  )

  return (
    <UpdateWizardPatientFileLockContext.Provider value={updateWizardPatientFileLock}>
      {isPatientFileLockNeeded && (
        <Suspense fallback={null}>
          <RequestTakeOverDialog
            patientId={patientId}
            patientName={patientName}
            onPatientFileLockTakeOverEnded={handlePatientFileLockTakeOverEnded}
          />
        </Suspense>
      )}
      {children}
    </UpdateWizardPatientFileLockContext.Provider>
  )
}

const useUpdateWizardPatientFileLock = (): UpdateWizardPatientFileLockContextValue => {
  const updateWizardPatientFileLock = useContext(UpdateWizardPatientFileLockContext)
  if (!updateWizardPatientFileLock) throw new Error("Missing UpdateWizardPatientFileLockProvider in the tree")

  return updateWizardPatientFileLock
}

type GiveAnswer = (patientFileLockTakeOverStatus: PatientFileLockTakeOverStatus) => void

const useWaitForPatientFileLock = () => {
  const waitingSubmitRef = useRef<GiveAnswer | null>(null)

  const answerWaitingSubmit = useCallback((patientFileLockTakeOverStatus: PatientFileLockTakeOverStatus): void => {
    const giveAnswer = waitingSubmitRef.current
    waitingSubmitRef.current = null

    giveAnswer?.(patientFileLockTakeOverStatus)
  }, [])

  const waitForPatientFileLock = useCallback((): Promise<PatientFileLockTakeOverStatus> => {
    answerWaitingSubmit("ABANDONED")

    const { promise, resolve } = Promise.withResolvers<PatientFileLockTakeOverStatus>()
    waitingSubmitRef.current = resolve

    return promise
  }, [answerWaitingSubmit])

  const reportPatientFileLockTakeOver = useCallback(
    (patientFileLockTakeOverStatus: PatientFileLockTakeOverStatus): void =>
      answerWaitingSubmit(patientFileLockTakeOverStatus),
    [answerWaitingSubmit],
  )

  useEffect(() => () => answerWaitingSubmit("ABANDONED"), [answerWaitingSubmit])

  return { waitForPatientFileLock, reportPatientFileLockTakeOver }
}

export { UpdateWizardPatientFileLockProvider, useUpdateWizardPatientFileLock }
